import { styles } from "./CarDetails.styles";
import CarDetailsLpDetection, { ILpPrediction } from "./LicensePlateDetection/CarDetailsLpDetection";
import { IZoomDataLayerEvents } from "../../CarSubmission";
import { SectionBadge } from "../../../SectionBadge";
import { SectionBadgeVariants } from "../../../SectionBadge/SectionBadge.style";
import { ZoomConfig } from "./LicensePlateDetection/utils/zoomConfig";
import { Icon, Icons } from "../../../../elements/Icon";
import { SyntheticEvent, useEffect, useRef, useState } from "react";

const carFallbackImage = require("./images/car-fallback.svg");

export interface ICarDetailsTranslations {
  partnerCarImageAlt: string;
  ariaLabelZoomInButton?: string;
  ariaLabelZoomOutButton?: string;
  imageHint: string;
  useTwoFingersHint?: string;
  partnerBadgeText: string;
  partnerBadgeImageAlt: string;
}

export interface ICarDetailsProps {
  partnerLogo?: string;
  carImage?: string;
  isZoomEnabled?: boolean;
  isLicensePlateDetection?: boolean;
  zoomDataLayerEvents?: IZoomDataLayerEvents;
  fetchLpPrediction?: (carImageUrl: string) => Promise<ILpPrediction>;
  translations: ICarDetailsTranslations;
}

export function CarDetails({
  partnerLogo,
  carImage = "",
  isZoomEnabled = false,
  isLicensePlateDetection = false,
  zoomDataLayerEvents,
  fetchLpPrediction,
  translations,
}: ICarDetailsProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isFallbackImage, setIsFallbackImage] = useState(false);

  const onImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = carFallbackImage;
    setIsFallbackImage(true);
  };

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth === 0) {
      img.src = carFallbackImage;
      setIsFallbackImage(true);
    }
  }, []);

  return (
    <div data-testid="car-details" className={styles.root()}>
      {partnerLogo && translations.partnerBadgeText && (
        <div className={styles.partnerLogo()}>
          <SectionBadge
            text={translations.partnerBadgeText}
            variant={SectionBadgeVariants.Partner}
            imageUrl={partnerLogo}
            imageAltText={translations.partnerBadgeImageAlt}
          />
        </div>
      )}
      {isZoomEnabled && isLicensePlateDetection && zoomDataLayerEvents && fetchLpPrediction ? (
        <>
          <CarDetailsLpDetection
            carImage={carImage}
            isLicensePlateDetection={isLicensePlateDetection}
            zoomDataLayerEvents={zoomDataLayerEvents}
            fetchLpPrediction={fetchLpPrediction}
            translations={translations}
          />
          {translations.imageHint && (
            <div className={styles.imageDescription()}>
              <Icon icon={Icons.infoBlack} />
              <p className={styles.imageText()}>{translations.imageHint}</p>
            </div>
          )}
        </>
      ) : (
        <div
          className={styles.carImage(isFallbackImage)}
          style={
            {
              "--width-for-desktop": `${ZoomConfig.windowWidth}px`,
              "--height-for-desktop": `${ZoomConfig.windowHeight}px`,
            } as React.CSSProperties
          }
        >
          <img
            ref={imgRef}
            src={carImage}
            alt={translations.partnerCarImageAlt}
            crossOrigin="anonymous"
            onError={onImageError}
          />
        </div>
      )}
    </div>
  );
}
